/** @jsxImportSource @emotion/react */
import { AccountMenu as KitAccountMenu, Button, DropdownMenuItem } from "@atomic/ui-kit"
import { signOut } from "aws-amplify/auth"
import { Eye, MessageSquareWarning, Shield } from "lucide-react"
import { useState, type FC } from "react"
import { useNavigate } from "react-router"
import { OPERATOR_ROLE, USER_ADMIN_ROLES, roleLabel } from "../../access/roles"
import { ViewAsDialog } from "../../containers/ViewAs/ViewAsDialog"
import { useAuth } from "../../hooks/useAuth"
import { useThemeMode } from "../../hooks/useThemeMode"
import { AuthenticationService } from "../../services/authentication"
import { isImpersonating } from "../../services/impersonation"

/** The account menu on the rail's floor: the kit's, with what only this app
 *  knows — the signed-in user and their role, the theme switch, User Admin and
 *  View As for the roles that have them, and log out. */
export const AccountMenu: FC = () => {
  const { user } = useAuth()
  const { mode, toggle } = useThemeMode()
  const navigate = useNavigate()
  const canManageUsers = !!user?.role && USER_ADMIN_ROLES.includes(user.role)
  const isOperator = user?.role === OPERATOR_ROLE
  const [viewAsOpen, setViewAsOpen] = useState(false)
  const impersonating = isImpersonating()

  return (
    <>
      <KitAccountMenu
        name={user?.name ?? null}
        email={user?.email ?? null}
        picture={user?.picture ?? null}
        detail={user?.role ? `Role: ${roleLabel(user.role)}` : undefined}
        themeMode={mode === "dark" ? "dark" : "light"}
        onToggleTheme={toggle}
        onLogout={() => {
          AuthenticationService.logout()
          signOut()
        }}
      >
        {canManageUsers ? (
          <>
            <DropdownMenuItem onSelect={() => navigate("/user-admin")}>
              <Shield size={16} aria-hidden />
              User Admin
            </DropdownMenuItem>
            {isOperator && !impersonating ? (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  setViewAsOpen(true)
                }}
              >
                <Eye size={16} aria-hidden />
                View As
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}
      </KitAccountMenu>
      <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />
    </>
  )
}

/** The speech-bubble feedback trigger beside the account menu; opens the
 *  FeedbackModal whose state lives in DashboardLayout (onSendFeedback). */
export const FeedbackButton: FC<{ onSendFeedback?: () => void }> = ({ onSendFeedback }) => (
  <Button type="button" variant="tertiaryText" size="iconSm" title="Send feedback" aria-label="Send feedback" onClick={() => onSendFeedback?.()}>
    <MessageSquareWarning size={16} aria-hidden />
  </Button>
)
